import { ESearchFilters } from '@/constants/searchFilters';
import { TQueryParams } from '@/redux/selectors/queryParams';

// Explicit allowlist of query keys that represent a user-set filter on the SRP.
// Derived from `ESearchFilters` values minus infrastructural keys (pagination,
// geo, sort, address, internal flags). Anything not in this set — UTM/affiliate
// params, and any future URL param anyone adds — is ignored.
const USER_FILTER_KEYS: readonly string[] = [
  ESearchFilters.DATE_FROM,
  ESearchFilters.DATE_TO,
  ESearchFilters.DATE_FROM_TIME,
  ESearchFilters.DATE_TO_TIME,
  ESearchFilters.FLEXIBLE_DAYS,
  ESearchFilters.SLEEPS,
  // GUESTS_ADULTS/CHILDREN/INFANTS share string values with SLEEPS_* so we
  // deliberately list only one alias each — otherwise countActiveFilters
  // double-counts guest params (they're not deduped by value).
  ESearchFilters.SLEEPS_ADULTS,
  ESearchFilters.SLEEPS_CHILDREN,
  ESearchFilters.SLEEPS_INFANTS,
  ESearchFilters.INSTANT_BOOK,
  ESearchFilters.DELIVERY,
  ESearchFilters.DELIVERY_ADDRESS,
  ESearchFilters.DELIVERY_STATIONARY,
  ESearchFilters.DELIVERY_QUERY,
  ESearchFilters.DELIVERY_CENTER,
  ESearchFilters.DELIVERY_DETAILS,
  ESearchFilters.DELIVERY_RADIUS,
  ESearchFilters.FILTER_FEATURE,
  ESearchFilters.FILTER_TAGS,
  ESearchFilters.FILTER_TAGS_SLUG,
  ESearchFilters.FILTER_TYPE,
  ESearchFilters.FILTER_STYLE,
  ESearchFilters.FILTER_KEYWORDS,
  ESearchFilters.FILTER_VEHICLE_MAKE,
  ESearchFilters.FILTER_VEHICLE_MAKE_MODELS,
  ESearchFilters.FILTER_VEHICLE_AMPS,
  ESearchFilters.FILTER_OWNERTYPE,
  ESearchFilters.FILTER_LINENS_INCLUDED,
  ESearchFilters.FILTER_RENTER_AGE,
  ESearchFilters.PRICE_MIN,
  ESearchFilters.PRICE_MAX,
  ESearchFilters.PRICE_TOTAL_MIN,
  ESearchFilters.PRICE_TOTAL_MAX,
  ESearchFilters.YEAR_GREATER,
  ESearchFilters.YEAR_LESSER,
  ESearchFilters.LENGTH_BETWEEN,
  ESearchFilters.LENGTH_GREATER,
  ESearchFilters.LENGTH_LESSER,
  ESearchFilters.WEIGHT_GREATER,
  ESearchFilters.WEIGHT_LESSER,
  ESearchFilters.TRAILER_WEIGHT,
  ESearchFilters.TOWING_CAPACITY,
  ESearchFilters.SEATBELTS,
  ESearchFilters.CANCEL_POLICY,
  ESearchFilters.INSURANCE_STATE,
  ESearchFilters.ALLOWS_LONG_TERM,
  ESearchFilters.NEW_OWNERS,
  ESearchFilters.UNLIMITED_MILES,
  ESearchFilters.UNLIMITED_GENERATOR,
  // OWNER_ID intentionally excluded — it scopes an owner-detail SRP, not a
  // user-applied filter, so it shouldn't add to activeFilterCount.
] as const;

// Mirrors the semantics of the individual chip guards: a filter is "set" if the
// query value is truthy in the everyday sense — non-empty string, not 'false',
// not '0', not `false`, not `undefined`.
const isFilterSet = (v: TQueryParams[keyof TQueryParams]): boolean => {
  if (v === undefined || v === null) return false;
  if (Array.isArray(v)) return v.some(isFilterSet);
  const s = String(v);
  if (s === '' || s === 'false' || s === '0') return false;
  return true;
};

export const countActiveFilters = (query: TQueryParams): number =>
  USER_FILTER_KEYS.filter(k => isFilterSet(query[k])).length;

// Child units aren't filtered individually, so the unit count only fits an unfiltered host search.
export const getDisplayTotal = (query: TQueryParams, total: number, totalUnits?: number): number =>
  query[ESearchFilters.OWNER_ID] && countActiveFilters(query) === 0 ? (totalUnits ?? total) : total;

interface IShouldTrackNoResultsViewed {
  isLoading: boolean;
  hasResults: boolean;
  hasError: boolean;
  pageOffset: number;
  metaTotal?: number;
  isPromoPage?: boolean;
  isNewPromoPage?: boolean;
}

export const shouldTrackNoResultsViewed = (o: IShouldTrackNoResultsViewed): boolean =>
  !o.isLoading &&
  !o.hasResults &&
  !o.hasError &&
  !(o.pageOffset > 0 && !!o.metaTotal) &&
  !o.isPromoPage &&
  !o.isNewPromoPage;
